import {useState, useCallback} from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Drawer, {DrawerProps} from '@mui/material/Drawer';

import {useBoolean} from 'src/hooks/use-boolean';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {styled} from '@mui/material/styles';

// ----------------------------------------------------------------------
const StyledLabel = styled('span')(({theme}) => ({
  ...theme.typography.caption,
  width: 100,
  flexShrink: 0,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightSemiBold
}));
interface Items {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  files?: {
    id: string;
    name: string;
    time?: string;
    score?: string;
    additionalFiles?: {id: string; name: string}[];
  }[];
}
type Props = DrawerProps & {
  item?: Items;
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  onClose: VoidFunction;
};

export default function ModuleDetails({item, open, onClose, ...other}: Props) {
  //   const {name, size, url, type, shared, modifiedAt} = item;

  const properties = useBoolean(true);

  const renderProperties = (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{typography: 'subtitle2'}}>
        Properties
        <IconButton size="small" onClick={properties.onToggle}>
          <Iconify
            icon={
              properties.value
                ? 'eva:arrow-ios-upward-fill'
                : 'eva:arrow-ios-downward-fill'
            }
          />
        </IconButton>
      </Stack>

      {properties.value && (
        <>
          {item?.name && (
            <Stack
              direction="row"
              sx={{typography: 'caption', textTransform: 'capitalize'}}>
              <Box
                component="span"
                sx={{width: 80, color: 'text.secondary', mr: 2}}>
                Name
              </Box>
              {item.name}
            </Stack>
          )}
          {item?.duration !== undefined && (
            <Stack
              direction="row"
              sx={{typography: 'caption', textTransform: 'capitalize'}}>
              <Box
                component="span"
                sx={{width: 80, color: 'text.secondary', mr: 2}}>
                Duration
              </Box>
              {item.duration}
            </Stack>
          )}
          {item?.description && (
            <Stack direction="row">
              <StyledLabel> Description </StyledLabel>
              <TextField
                fullWidth
                multiline
                size="small"
                value={item.description}
                InputProps={{
                  sx: {typography: 'body2', minHeight: '12vh'}
                }}
                sx={{minHeight: '12vh'}}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: {invisible: true}
        }}
        PaperProps={{
          sx: {width: '40vw'}
        }}
        {...other}>
        <Scrollbar sx={{height: 1}}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{p: 2.5}}>
            <Typography variant="h6"> Info </Typography>
          </Stack>
          <Stack
            spacing={2.5}
            justifyContent="center"
            sx={{
              p: 2.5,
              bgcolor: 'background.neutral'
            }}>
            {renderProperties}
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
